import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { FileText, TrendingUp, Eye, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const VISIBILITY_LABELS = {
  ozet: 'Özet',
  tam_detay: 'Tam Detay',
};

const PAYMENT_STATUS_LABELS = {
  bekliyor: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700' },
  odendi: { label: 'Ödendi', color: 'bg-green-100 text-green-700' },
  kismi: { label: 'Kısmi', color: 'bg-blue-100 text-blue-700' },
};

export const UserExpenses = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filterCategory]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.append('category', filterCategory);

      const [expRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/expenses?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/expenses/categories`, { withCredentials: true }),
      ]);
      setExpenses(expRes.data);
      setCategories(catRes.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const totalAmount = expenses.reduce((s, e) => s + e.amount, 0);

  const getStatusBadge = (s) => {
    const opt = PAYMENT_STATUS_LABELS[s];
    return opt ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : s;
  };

  if (loading) {
    return <PageLayout><div className="flex items-center justify-center min-h-[50vh]"><p className="text-[#595959]">Yükleniyor...</p></div></PageLayout>;
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" data-testid="user-expenses-title">
            Site Giderleri
          </h1>
          <p className="text-sm text-[#595959] mt-1">Apartman/site adına yapılan harcamalar</p>
        </div>

        {/* Summary */}
        <Card className="border border-[#E5E5E5] mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#595959]">Paylaşılan Toplam Gider</p>
                <p className="text-2xl font-bold text-[#1A1A2E]" data-testid="user-total-expense">{fmt(totalAmount)} TL</p>
              </div>
              <TrendingUp className="w-6 h-6 text-[#2563EB]" />
            </div>
            <p className="text-xs text-[#595959] mt-1">{expenses.length} kayıt paylaşıldı</p>
          </CardContent>
        </Card>

        {/* Filter */}
        <div className="mb-4">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px]" data-testid="user-filter-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Expense List */}
        <div className="space-y-3">
          {expenses.length === 0 ? (
            <Card className="border border-[#E5E5E5]">
              <CardContent className="py-12 text-center">
                <FileText className="w-10 h-10 text-[#D0D0D0] mx-auto mb-3" />
                <p className="text-[#595959]">Henüz paylaşılan gider bulunmuyor</p>
              </CardContent>
            </Card>
          ) : expenses.map((exp) => (
            <Card key={exp.id} className="border border-[#E5E5E5]" data-testid={`user-expense-${exp.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A1A2E]">{exp.name}</p>
                    <p className="text-xs text-[#595959]">{exp.category_label} - {exp.date}</p>
                  </div>
                  <p className="font-bold text-lg text-[#1A1A2E] ml-3 whitespace-nowrap">{fmt(exp.amount)} TL</p>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  {getStatusBadge(exp.payment_status)}
                  <span className="text-xs text-[#595959] flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {VISIBILITY_LABELS[exp.visibility] || exp.visibility}
                  </span>
                </div>

                {/* Full details for tam_detay */}
                {exp.visibility === 'tam_detay' && (
                  <>
                    {exp.description && (
                      <p className="text-sm text-[#595959] mb-2 bg-[#F9F8F6] p-3 rounded-lg">{exp.description}</p>
                    )}
                    {exp.payment_account && (
                      <p className="text-xs text-[#595959] mb-2">Ödeme: {exp.payment_account}</p>
                    )}
                    {exp.documents && exp.documents.length > 0 && (
                      <div className="space-y-1 mt-2">
                        <p className="text-xs font-medium text-[#595959]">Belgeler:</p>
                        {exp.documents.map((doc) => (
                          <a
                            key={doc}
                            href={`${API_URL}/api/expenses/files/${exp.business_id}/${doc}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 text-sm text-[#2563EB] hover:underline bg-[#F9F8F6] px-3 py-1.5 rounded"
                            data-testid={`doc-link-${doc}`}
                          >
                            <FileText className="w-4 h-4" />
                            <span className="truncate flex-1">{doc}</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Summary note for ozet */}
                {exp.visibility === 'ozet' && (
                  <p className="text-xs text-[#999] italic mt-1">Özet olarak paylaşılmıştır</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};
